import React, { useState } from 'react';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import { Box, Button, Popover, TextField, InputAdornment, MenuItem, MenuList, Typography } from '@material-ui/core';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import moment from 'moment';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { SIMPLE_DATE_FORMAT } from '../../utils/utils';
import { RANGE_PRESETS, findMatchingPresetKey, isRangeValid } from './dateRangePresets';

export const DateRangePicker = ({ onChange = () => {}, value = {}, className }) => {
  const { messages: intlMessages } = useIntl();
  const [anchorEl, setAnchorEl] = useState(null);
  const [draftStart, setDraftStart] = useState();
  const [draftEnd, setDraftEnd] = useState();
  const open = Boolean(anchorEl);

  const selectedPresetKey = findMatchingPresetKey(value.startDate, value.endDate);
  const isDraftValid = isRangeValid(draftStart, draftEnd);
  const isDraftInverted = !!draftStart && !!draftEnd && !isDraftValid;

  const handleOpen = event => {
    setDraftStart(value.startDate);
    setDraftEnd(value.endDate);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleStartDateChange = date => setDraftStart(date);

  const handleEndDateChange = date => setDraftEnd(date);

  const handleApply = () => {
    onChange({ startDate: draftStart, endDate: draftEnd });
    handleClose();
  };

  const handlePresetChange = ({ getRange }) => {
    const { start, end } = getRange();

    setDraftStart(start);
    setDraftEnd(end);
    onChange({ startDate: start, endDate: end });
    handleClose();
  };

  return (
    <MuiPickersUtilsProvider utils={MomentUtils}>
      <TextField
        className={className}
        label={intlMessages['common.dateRange']}
        fullWidth
        onClick={handleOpen}
        value={
          value.startDate && value.endDate
            ? `${moment(value.startDate).format(SIMPLE_DATE_FORMAT)} - ${moment(value.endDate).format(SIMPLE_DATE_FORMAT)}`
            : ''
        }
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <CalendarTodayIcon color="action" />
            </InputAdornment>
          ),
          readOnly: true
        }}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box p={2} display="flex" flexDirection="column" style={{ width: 'fit-content' }}>
          <Box display="flex" flexWrap={'nowrap'}>
            <Box>
              <MenuList>
                {RANGE_PRESETS.map(preset => (
                  <MenuItem key={preset.key} selected={selectedPresetKey === preset.key} onClick={() => handlePresetChange(preset)}>
                    <FormattedMessage id={`datepicker.${preset.key}`} />
                  </MenuItem>
                ))}
              </MenuList>
            </Box>
            <KeyboardDatePicker
              disableToolbar
              variant="static"
              label={intlMessages['contract.start_date']}
              value={draftStart}
              onChange={handleStartDateChange}
              format={SIMPLE_DATE_FORMAT}
              emptyLabel={SIMPLE_DATE_FORMAT}
              style={{ marginRight: '16px' }}
              autoOk
            />
            <KeyboardDatePicker
              disableToolbar
              variant="static"
              label={intlMessages['contract.end_date']}
              value={draftEnd}
              onChange={handleEndDateChange}
              format={SIMPLE_DATE_FORMAT}
              emptyLabel={SIMPLE_DATE_FORMAT}
              minDate={draftStart || undefined}
              autoOk
            />
          </Box>
          <Box display="flex" alignItems="center" justifyContent="flex-end" pt={1} style={{ gap: '8px' }}>
            {isDraftInverted && (
              <Typography variant="caption" color="error">
                <FormattedMessage id="datepicker.invalidRange" />
              </Typography>
            )}
            <Button onClick={handleClose}>
              <FormattedMessage id="datepicker.cancel" />
            </Button>
            <Button color="primary" variant="contained" disabled={!isDraftValid} onClick={handleApply}>
              <FormattedMessage id="datepicker.apply" />
            </Button>
          </Box>
        </Box>
      </Popover>
    </MuiPickersUtilsProvider>
  );
};

DateRangePicker.propTypes = {
  onChange: PropTypes.func,
  value: PropTypes.shape({
    startDate: PropTypes.object,
    endDate: PropTypes.object
  }),
  className: PropTypes.string
};

DateRangePicker.defaultProps = {
  onChange: () => {},
  value: {},
  className: undefined
};
